import {createSlice, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {TPolarisPhaseName, TTranscriptMessage} from "api/polarisApi/types";
import {LOGOUT} from "store/mapXActionTypes";
import {isFootprintStateConflict} from "store/polaris/footprintGeneration";
import {addCalibrationMatchers} from "store/polaris/calibrationReducers";
import {addCriteriaMatchers} from "store/polaris/criteriaReducers";
import {addFootprintMatchers} from "store/polaris/footprintReducers";
import {addRankedListMatchers} from "store/polaris/rankedListReducers";
import type {TPolarisReducerState, TSseStatus} from "store/polaris/types";
import * as thunks from "store/polaris/polarisThunks";

export const polarisInitialState: TPolarisReducerState = {
	search_id: null,
	current_phase: "entry",
	bootstrap_status: "idle",
	transcript: [],
	head_seq: 0,
	sse_status: "idle",
	role_basics: null,
	footprint: null,
	footprint_epoch: 0,
	footprint_read_seq: 0,
	calibration: null,
	calibration_sampling: "idle",
	criteria: null,
	search_run: {
		status: null,
		polling: false,
		ruleouts: null,
		drilldowns: {},
		candidates: [],
		candidates_total: null,
		sort: null,
	},
	projects: [],
	handoff: null,
	saved_project: null,
	recent_searches: {
		results: [],
		pagination: null,
		loading: false,
		error: null,
	},
	active_calibration_index: 0,
	is_working: false,
	confirming: false,
	loading_phase: null,
	load_error: null,
	error: null,
};

const insertBySeq = (transcript: TTranscriptMessage[], message: TTranscriptMessage) => {
	if (transcript.some((m) => m.seq === message.seq)) return;

	transcript.push(message);
	transcript.sort((a, b) => a.seq - b.seq);
};

const polarisSlice = createSlice({
	name: "polaris",
	initialState: polarisInitialState,
	reducers: {
		transcriptMessageReceived: (state, action: PayloadAction<TTranscriptMessage>) => {
			insertBySeq(state.transcript, action.payload);
			state.head_seq = Math.max(state.head_seq, action.payload.seq);
		},
		phaseChanged: (state, action: PayloadAction<TPolarisPhaseName>) => {
			state.current_phase = action.payload;
		},
		sseStatusChanged: (state, action: PayloadAction<TSseStatus>) => {
			state.sse_status = action.payload;
		},
		calibrationIndexChanged: (state, action: PayloadAction<number>) => {
			state.active_calibration_index = action.payload;
		},
		searchPollingStopped: (state) => {
			state.search_run.polling = false;
		},
		errorCleared: (state) => {
			state.error = null;
		},
		searchCleared: () => ({...polarisInitialState}),
	},
	extraReducers: (builder) => {
		builder
			.addCase(thunks.createSearch.fulfilled, (state, action) => {
				return {
					...polarisInitialState,
					search_id: action.payload.search_id,
					current_phase: action.payload.current_phase,
					bootstrap_status: "ready",
				};
			})
			.addCase(thunks.bootstrapSearch.pending, (state, action) => {
				state.bootstrap_status = "loading";
				state.search_id = action.meta.arg;
			})
			.addCase(thunks.bootstrapSearch.fulfilled, (state, action) => {
				state.bootstrap_status = "ready";
				state.transcript = action.payload.messages;
				state.head_seq = action.payload.head_seq;
				state.current_phase = action.payload.current_phase;
			})
			.addCase(thunks.bootstrapSearch.rejected, (state) => {
				state.bootstrap_status = "error";
			})
			// Echo the user's message locally as soon as the server accepts it,
			// so it shows even before (or without) the SSE replay.
			.addCase(thunks.sendMessage.fulfilled, (state, action) => {
				insertBySeq(state.transcript, {
					message_id: action.payload.message_id,
					seq: action.payload.seq,
					phase: state.current_phase,
					kind: "user",
					body: {text: action.meta.arg},
					created_at: action.payload.accepted_at,
				});
				state.head_seq = Math.max(state.head_seq, action.payload.seq);
			})
			// Track which phase's data is in flight so the workspace can show a
			// skeleton for that phase only (not the one being left on Proceed).
			// A retry clears any prior error for the same phase.
			.addCase(thunks.loadPhaseData.pending, (state, action) => {
				state.loading_phase = action.meta.arg;

				if (state.load_error?.phase === action.meta.arg) state.load_error = null;
			})
			.addCase(thunks.loadPhaseData.fulfilled, (state, action) => {
				state.loading_phase = null;

				if (state.load_error?.phase === action.payload) state.load_error = null;
			})
			// loadPhaseData unwraps its inner loads, so it rejects when a phase's
			// data fails to load — record it so the workspace can offer a retry.
			.addCase(thunks.loadPhaseData.rejected, (state, action) => {
				state.loading_phase = null;
				state.load_error = {
					phase: action.meta.arg,
					message: action.error?.message || "Could not load this phase",
				};
			})
			.addCase(thunks.loadRoleBasics.fulfilled, (state, action) => {
				state.role_basics = action.payload;
			})
			.addCase(thunks.updateRoleField.fulfilled, (state, action) => {
				state.role_basics = action.payload;
			})
			// confirming spans the whole transition (confirm call + next phase
			// load) so the workspace shows a skeleton for the incoming phase.
			.addCase(thunks.confirmPhase.pending, (state) => {
				state.confirming = true;
			})
			.addCase(thunks.confirmPhase.fulfilled, (state, action) => {
				state.current_phase = action.payload.current_phase;
				state.confirming = false;
			})
			.addCase(thunks.confirmPhase.rejected, (state) => {
				state.confirming = false;
			})
			// Polling is armed by the poll thunk itself (not by phase loads) so
			// it survives reloads mid-Phase 5; the self-rescheduling timer in
			// the thunk checks this flag before each round.
			.addCase(thunks.pollSearchStatus.pending, (state) => {
				state.search_run.polling = true;
			})
			.addCase(thunks.pollSearchStatus.rejected, (state) => {
				state.search_run.polling = false;
			})
			.addCase(thunks.pollSearchStatus.fulfilled, (state, action) => {
				state.search_run.status = action.payload;

				const done = action.payload.stages.every((stage) => stage.status === "done");

				if (done) state.search_run.polling = false;
			})
			.addCase(thunks.loadRuleouts.fulfilled, (state, action) => {
				state.search_run.ruleouts = action.payload;
			})
			.addCase(thunks.loadRuleoutCandidates.fulfilled, (state, action) => {
				state.search_run.drilldowns[action.payload.ruleout_id] = action.payload;
			})
			.addCase(thunks.loadProjects.fulfilled, (state, action) => {
				state.projects = action.payload.projects;
			})
			.addCase(thunks.loadHandoffSummary.fulfilled, (state, action) => {
				state.handoff = action.payload;
			})
			.addCase(thunks.saveToProject.fulfilled, (state, action) => {
				state.saved_project = action.payload.project;
			})
			.addCase(thunks.loadRecentSearches.pending, (state) => {
				state.recent_searches.loading = true;
				state.recent_searches.error = null;
			})
			.addCase(thunks.loadRecentSearches.fulfilled, (state, action) => {
				const {results, pagination} = action.payload;

				// The list is paginated (discrete pages), so each load replaces
				// the visible page rather than appending.
				state.recent_searches.results = results;
				state.recent_searches.pagination = pagination;
				state.recent_searches.loading = false;
			})
			.addCase(thunks.loadRecentSearches.rejected, (state, action) => {
				state.recent_searches.loading = false;
				state.recent_searches.error =
					action.error?.message || "Could not load recent searches";
			})
			.addCase(LOGOUT, () => ({...polarisInitialState}));

		// Footprint reads (stale-read guard) and mutations (epoch bookkeeping).
		addFootprintMatchers(builder);
		// Calibration sample and carousel position.
		addCalibrationMatchers(builder);
		// Criteria list, the criterion added from free text, the Essential flag.
		addCriteriaMatchers(builder);
		// Ranked list pages and the order they are read in.
		addRankedListMatchers(builder);

		builder
			.addMatcher(
				isAnyOf(
					thunks.createSearch.pending,
					thunks.confirmPhase.pending,
					thunks.resample.pending,
					thunks.saveToProject.pending,
				),
				(state) => {
					state.is_working = true;
					state.error = null;
				},
			)
			.addMatcher(
				isAnyOf(
					thunks.createSearch.fulfilled,
					thunks.confirmPhase.fulfilled,
					thunks.resample.fulfilled,
					thunks.saveToProject.fulfilled,
				),
				(state) => {
					state.is_working = false;
				},
			)
			.addMatcher(
				(action): action is PayloadAction<unknown> & {error: {message?: string}} =>
					action.type.startsWith("polaris/") &&
					action.type.endsWith("/rejected") &&
					// Recent-searches failures own their own error slot — keep them
					// out of the shared error (it drives the brief input's message).
					!action.type.startsWith("polaris/loadRecentSearches") &&
					// Rolled back and toasted by the Essential toggle.
					!thunks.setCriterionEssential.rejected.match(action) &&
					// Silent: the next poll retries; the footprint failed banner and
					// the calibration sample panel report a failed retry themselves.
					!thunks.refreshFootprint.rejected.match(action) &&
					!thunks.retryFootprint.rejected.match(action) &&
					!thunks.retryCalibration.rejected.match(action) &&
					// Toasted by the add forms, which keep the draft.
					!thunks.addCriterionFromText.rejected.match(action) &&
					!thunks.addClusterFromText.rejected.match(action),
				(state, action) => {
					state.is_working = false;

					// "Footprint not in the state you assumed" (409 with a code) is
					// handled by a silent refresh, not surfaced as an error.
					if (isFootprintStateConflict(action.error)) return;

					state.error = action.error?.message || "Something went wrong";
				},
			);
	},
});

export const {
	transcriptMessageReceived,
	phaseChanged,
	sseStatusChanged,
	calibrationIndexChanged,
	searchPollingStopped,
	errorCleared,
	searchCleared,
} = polarisSlice.actions;

export default polarisSlice.reducer;
